import { create } from 'zustand';

import { ApiEndpoints } from '@lib/enums/ApiEndpoints';
import type { ModelType } from '@lib/enums/ModelType';
import { UserPermissions, type UserRoles } from '@lib/enums/Roles';
import { apiUrl } from '@lib/functions/Api';
import type { UserProps, UserStateProps } from '@lib/types/User';
import { api, setApiDefaults } from '../App';
import { clearCsrfCookie } from '../functions/auth';

// Only in-flight native session/profile reads are coordinated here. No
// credential persistence is added. Logout cancels both and rejects stale reads.
let profileReadEpoch = 0;
let pendingProfileRead: {
  epoch: number;
  promise: Promise<void>;
  cancel: () => void;
} | null = null;
let pendingSessionRead: {
  epoch: number;
  promise: Promise<void>;
  cancel: () => void;
} | null = null;
const profileRetryDelays = [500, 1000, 2000];

function isTransientSessionReadFailure(error: unknown) {
  const failure = error as { code?: string; response?: { status?: number } };
  const status = failure.response?.status;
  if (status !== undefined) return status === 408 || (status >= 500 && status < 600);
  // Unknown errors and explicit authentication denial never enter recovery.
  return ['ECONNABORTED', 'ETIMEDOUT', 'ECONNRESET', 'ERR_NETWORK', 'ERR_CANCELED']
    .includes(failure.code ?? '');
}

function invalidateProfileRead() {
  profileReadEpoch++;
  pendingProfileRead?.cancel();
  pendingSessionRead?.cancel();
}

function waitForProfileRetry(signal: AbortSignal, delay?: number) {
  return new Promise<void>((resolve) => {
    let timer: number | undefined;
    const finish = () => {
      if (timer !== undefined) window.clearTimeout(timer);
      signal.removeEventListener('abort', finish);
      window.removeEventListener('online', finish);
      window.removeEventListener('focus', finish);
      resolve();
    };
    signal.addEventListener('abort', finish, { once: true });
    if (delay !== undefined) timer = window.setTimeout(finish, delay);
    else {
      // After four attempts, pause instead of polling. A real reconnect/focus
      // resumes one bounded series; refresh keeps the native bootstrap path.
      window.addEventListener('online', finish, { once: true });
      window.addEventListener('focus', finish, { once: true });
    }
    if (signal.aborted) finish();
  });
}

/**
 * Global user information state, using Zustand manager
 */
export const useUserState = create<UserStateProps>((set, get) => ({
  user: undefined,
  is_authed: false,
  setAuthenticated: (authed = true) => {
    if (get().isAuthed() !== authed) invalidateProfileRead();
    set({ is_authed: authed });
    setApiDefaults();
  },
  userId: () => {
    const user: UserProps = get().user as UserProps;
    return user?.pk;
  },
  username: () => {
    const user: UserProps = get().user as UserProps;

    if (user?.first_name || user?.last_name) {
      return `${user.first_name} ${user.last_name}`.trim();
    } else {
      return user?.username ?? '';
    }
  },
  setUser: (newUser: UserProps | undefined) => set({ user: newUser }),
  getUser: () => get().user,
  clearUserState: () => {
    invalidateProfileRead();
    set({ user: undefined, is_authed: false });
    clearCsrfCookie();
    setApiDefaults();
  },
  fetchUserToken: async () => {
    // If neither the csrf or session cookies are available, we cannot fetch a token
    if (
      !document.cookie.includes('csrftoken') &&
      !document.cookie.includes('sessionid')
    ) {
      get().setAuthenticated(false);
      return;
    }

    const epoch = profileReadEpoch;
    if (pendingSessionRead?.epoch === epoch) return pendingSessionRead.promise;
    const controller = new AbortController();
    const current = () => epoch === profileReadEpoch;
    const readSession = async () => {
      let failures = 0;
      while (current()) {
        try {
          const response = await api.get(apiUrl(ApiEndpoints.auth_session), {
            timeout: 5000,
            signal: controller.signal
          });
          if (!current()) return;
          // Only the real native session response may authenticate bootstrap.
          if (response.status === 200 && response.data.meta.is_authenticated) {
            get().setAuthenticated(true);
          } else {
            get().clearUserState();
          }
          return;
        } catch (error) {
          if (!current()) return;
          if (!isTransientSessionReadFailure(error)) {
            get().clearUserState();
            return;
          }
          // Leave the native initialization pending; do not turn a failed
          // read into authentication-invalid or fabricate a logged-in user.
          if (failures < profileRetryDelays.length) {
            await waitForProfileRetry(controller.signal, profileRetryDelays[failures++]);
          } else {
            await waitForProfileRetry(controller.signal);
            failures = 0;
            if (current()) await waitForProfileRetry(controller.signal, 2000);
          }
        }
      }
    };
    const promise = readSession();
    pendingSessionRead = { epoch, promise, cancel: () => controller.abort() };
    try {
      await promise;
    } finally {
      if (pendingSessionRead?.promise === promise) pendingSessionRead = null;
    }
  },
  fetchUserState: async () => {
    if (!get().isAuthed()) {
      await get().fetchUserToken();
    }

    // If we still don't have a token, clear the user state and return
    if (!get().isAuthed()) {
      get().clearUserState();
      return;
    }

    // Deduplicate reads within the current authenticated context. A response
    // from a cleared/changed context may never restore a logged-out user.
    const epoch = profileReadEpoch;
    if (pendingProfileRead?.epoch === epoch) return pendingProfileRead.promise;
    const controller = new AbortController();
    const current = () => epoch === profileReadEpoch && get().isAuthed();
    const readProfile = async () => {
      let failures = 0;
      while (current()) {
        try {
          const response = await api.get(apiUrl(ApiEndpoints.user_me), {
            params: { roles: true },
            // Preserve the native initial deadline. Recovery reads use the
            // existing general API budget; the same four-attempt bound, epoch
            // checks, immediate401 clearing and logout cancellation still apply.
            timeout: failures === 0 ? 2000 : 5000,
            signal: controller.signal
          });
          if (!current()) return;
          if (response.status !== 200) return;
          const user: UserProps = {
            pk: response.data.pk,
            first_name: response.data?.first_name ?? '',
            last_name: response.data?.last_name ?? '',
            email: response.data.email,
            username: response.data.username,
            groups: response.data.groups,
            profile: response.data.profile,
            roles: response.data?.roles ?? {},
            permissions: response.data?.permissions ?? {},
            is_staff: response.data?.is_staff ?? false,
            is_superuser: response.data?.is_superuser ?? false
          };
          get().setUser(user);
          return;
        } catch (error) {
          if (!current()) return;
          const status = (error as { response?: { status?: number } }).response?.status;
          // The native UserMe contract uses401 for absent/expired/revoked
          // authentication (Token WWW-Authenticate).403 is not established as
          // authentication invalid by that contract; do not relabel it as
          // transient or change backend authorization denial semantics.
          if (status === 401) {
            get().clearUserState();
            return;
          }
          const transient = status === undefined || status === 408 ||
            (status >= 500 && status < 600);
          if (!transient) return;
          // A transport/read error cannot authenticate an anonymous user:
          // current() requires the already validated native auth context.
          if (failures < profileRetryDelays.length) {
            await waitForProfileRetry(controller.signal, profileRetryDelays[failures++]);
          } else {
            await waitForProfileRetry(controller.signal);
            failures = 0;
            // Bound repeated focus/reconnect events as well as network errors.
            if (current()) await waitForProfileRetry(controller.signal, 2000);
          }
        }
      }
    };
    const promise = readProfile();
    pendingProfileRead = { epoch, promise, cancel: () => controller.abort() };
    try {
      await promise;
    } finally {
      if (pendingProfileRead?.promise === promise) pendingProfileRead = null;
    }
  },
  isAuthed: () => {
    return get().is_authed;
  },
  isLoggedIn: () => {
    if (!get().isAuthed()) {
      return false;
    }
    const user: UserProps = get().user as UserProps;
    return !!user && !!user.pk;
  },
  isStaff: () => {
    const user: UserProps = get().user as UserProps;
    return user?.is_staff ?? false;
  },
  isSuperuser: () => {
    const user: UserProps = get().user as UserProps;
    return user?.is_superuser ?? false;
  },
  checkUserRole: (role: UserRoles, permission: UserPermissions) => {
    // Check if the user has the specified permission for the specified role
    const user: UserProps = get().user as UserProps;

    if (!user) {
      return false;
    }

    if (user?.is_superuser) return true;
    if (user?.roles === undefined) return false;
    if (user?.roles[role] === undefined) return false;
    if (user?.roles[role] === null) return false;

    return user?.roles[role]?.includes(permission) ?? false;
  },
  hasDeleteRole: (role: UserRoles) => {
    return get().checkUserRole(role, UserPermissions.delete);
  },
  hasChangeRole: (role: UserRoles) => {
    return get().checkUserRole(role, UserPermissions.change);
  },
  hasAddRole: (role: UserRoles) => {
    return get().checkUserRole(role, UserPermissions.add);
  },
  hasViewRole: (role: UserRoles) => {
    return get().checkUserRole(role, UserPermissions.view);
  },
  checkUserPermission: (model: ModelType, permission: UserPermissions) => {
    // Check if the user has the specified permission for the specified model
    const user: UserProps = get().user as UserProps;

    if (!user) {
      return false;
    }

    if (user?.is_superuser) return true;

    if (user?.permissions === undefined) return false;
    if (user?.permissions[model] === undefined) return false;
    if (user?.permissions[model] === null) return false;

    return user?.permissions[model]?.includes(permission) ?? false;
  },
  hasDeletePermission: (model: ModelType) => {
    return get().checkUserPermission(model, UserPermissions.delete);
  },
  hasChangePermission: (model: ModelType) => {
    return get().checkUserPermission(model, UserPermissions.change);
  },
  hasAddPermission: (model: ModelType) => {
    return get().checkUserPermission(model, UserPermissions.add);
  },
  hasViewPermission: (model: ModelType) => {
    return get().checkUserPermission(model, UserPermissions.view);
  },
  // login state
  login_checked: false,
  setLoginChecked: (value) => {
    set({ login_checked: value });
  }
}));
